import React, { useState, useEffect } from 'react'
import { Stack, Button, Input, Textarea, SimpleModal, FormControl, Box, Icon, Select } from '@/components/lib'
import { FaTrash, FaPlus } from 'react-icons/fa'
import api from '../../../api'
import utils from '../../../utils/utils'

const EXTENDED_FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'integer', label: 'Integer' },
  { value: 'float', label: 'Float' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & time' },
  { value: 'boolean', label: 'Boolean' }
]

export default function PackTypeForm ({ trialId, packType, onClose, onCreated, onUpdated }) {
  const isEdit = !!packType
  const [name, setName] = useState(packType?.name ?? '')
  const [description, setDescription] = useState(packType?.description ?? '')
  const [extendedFields, setExtendedFields] = useState(
    packType?.extendedFields?.length ? [...packType.extendedFields] : [{ key: '', label: '', type: 'text' }]
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (packType?.extendedFields?.length) {
      setExtendedFields(packType.extendedFields.map((f) => ({ ...f })))
    }
  }, [packType?._id])

  function addExtendedField () {
    setExtendedFields((prev) => [...prev, { key: '', label: '', type: 'text' }])
  }

  function removeExtendedField (index) {
    setExtendedFields((prev) => prev.filter((_, i) => i !== index))
  }

  function updateExtendedField (index, field, value) {
    setExtendedFields((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  function save () {
    if (!name?.trim()) {
      utils.toast('error', 'Name is required')
      return
    }
    const fields = extendedFields.filter((f) => f.key?.trim() && f.label?.trim())
    setSaving(true)
    if (isEdit) {
      api.packs.updatePackType(
        packType._id,
        { name: name.trim(), description: description.trim() || null, extendedFields: fields },
        (updated) => {
          setSaving(false)
          onUpdated(updated)
        },
        (err) => {
          setSaving(false)
          utils.toast('error', err.message)
        }
      )
    } else {
      api.packs.createPackType(
        trialId,
        { name: name.trim(), description: description.trim() || null, extendedFields: fields },
        (created) => {
          setSaving(false)
          onCreated(created)
        },
        (err) => {
          setSaving(false)
          utils.toast('error', err.message)
        }
      )
    }
  }

  return (
    <SimpleModal
      size='lg'
      isOpen
      onClose={onClose}
      header={isEdit ? 'Edit pack type' : 'Add pack type'}
      body={
        <Stack gap={4}>
          <FormControl label='Name' required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. Medication pack' />
          </FormControl>
          <FormControl label='Description'>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder='Optional description' rows={2} />
          </FormControl>
          <Box>
            <FormControl label='Extended fields (optional)' />
            <Stack gap={2}>
              {extendedFields.map((f, i) => (
                <Stack key={i} direction='row' gap={2} alignItems='center'>
                  <Input
                    size='sm'
                    placeholder='Key (e.g. expiryDate)'
                    value={f.key}
                    onChange={(e) => updateExtendedField(i, 'key', e.target.value)}
                    minW='120px'
                  />
                  <Input
                    size='sm'
                    placeholder='Label'
                    value={f.label}
                    onChange={(e) => updateExtendedField(i, 'label', e.target.value)}
                    minW='200px'
                  />
                  <Select
                    value={f.type}
                    onChange={(e) => updateExtendedField(i, 'type', e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', minWidth: 100 }}
                  >
                    {EXTENDED_FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Select>
                  <Button size='xs' variant='ghost' onClick={() => removeExtendedField(i)}>
                    <Icon as={FaTrash} />
                  </Button>
                </Stack>
              ))}
              <Button size='xs' variant='outline' colorPalette='primary' leftIcon={<Icon as={FaPlus} />} onClick={addExtendedField}>
                Add field
              </Button>
            </Stack>
          </Box>
        </Stack>
      }
      footer={
        <Stack direction='row' justifyContent='end' gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button colorPalette='primary' onClick={save} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </Stack>
      }
    />
  )
}
